import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, MoonStar } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { AccessExpired } from "../components/AccessExpired";
import { toast } from "sonner";

const PINK = "#D97B8F";

export default function Matches() {
  const [matches, setMatches] = useState(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    api.get("/matches").then((r) => setMatches(r.data)).catch((e) => {
      if (e?.response?.status === 402) setExpired(true);
      else toast.error(formatApiError(e));
    });
  }, []);

  if (expired) return <AccessExpired />;

  return (
    <div className="relative min-h-[calc(100vh-64px)] overflow-hidden bg-gradient-to-b from-[#FBEAE8] via-cream to-[#F9E3E1]" data-testid="matches-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full object-cover opacity-[0.14]" />
      <img loading="lazy" src="/assets/mandala-arrow.jpeg" alt="" className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-[0.05]" />

      <div className="relative mx-auto max-w-5xl px-4 py-12 sm:px-8">
        <div className="text-center">
          <p className="font-cinzel text-xs tracking-[0.35em] text-gold">VOS RENCONTRES</p>
          <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Mes matchs</h1>
          <p className="mx-auto mt-3 max-w-md font-serif text-base italic text-clay">
            Deux cartes retournées, deux cœurs réciproques — la conversation commence ici.
          </p>
          <div className="mx-auto mt-5 h-px w-24 bg-gradient-to-r from-transparent via-[#D97B8F] to-transparent" />
        </div>

        {matches === null ? (
          <p className="mt-16 text-center text-sm text-stone2">Chargement…</p>
        ) : matches.length === 0 ? (
          <div className="mx-auto mt-12 max-w-lg rounded-3xl border border-[#D97B8F]/30 bg-white/90 p-10 text-center shadow-card backdrop-blur-sm" data-testid="matches-empty">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#D97B8F]/15">
              <MoonStar size={30} className="text-[#D97B8F]" />
            </div>
            <h2 className="mt-5 font-serif text-2xl font-medium text-ink">Vos belles rencontres se préparent</h2>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-clay">Les profils choisis pour vous selon vos affinités vous attendent dans « Découvrir » : retournez leurs cartes, envoyez un coup de cœur — et si c'est réciproque, la conversation s'ouvrira ici.</p>
            <Link to="/discover" data-testid="matches-discover-link" className="mt-7 inline-block rounded-full bg-[#D97B8F] px-7 py-3 text-sm font-semibold text-white shadow-boho transition-all duration-300 hover:scale-[1.03] hover:bg-[#C4637A]">
              Découvrir des profils
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {matches.map((m, i) => (
              <motion.div key={m.match_id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.5 }}>
                <Link
                  to={`/chat/${m.match_id}`}
                  data-testid={`match-card-${i}`}
                  className="group flex items-center gap-4 rounded-3xl border border-[#D97B8F]/25 bg-white/90 p-5 shadow-boho backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#D97B8F]/70 hover:shadow-card"
                >
                  {m.avatar ? (
                    <img src={m.avatar} alt={m.name} className="h-16 w-16 rounded-full border-2 border-[#D97B8F]/50 object-cover" />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#D97B8F]/50 bg-[#FBEAE8] font-serif text-2xl text-[#C4637A]">
                      {m.name?.[0]}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="truncate font-serif text-lg font-semibold text-ink">{m.name}</h3>
                      {m.unread > 0 && (
                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#D97B8F] px-1.5 text-[10px] font-bold text-white" data-testid={`match-unread-${i}`}>
                          {m.unread}
                        </span>
                      )}
                      {m.compatibility != null && (
                        <span className="shrink-0 rounded-full bg-[#D97B8F]/12 px-2 py-0.5 text-xs font-semibold text-[#C4637A]">{m.compatibility}%</span>
                      )}
                    </div>
                    <p className="font-cinzel text-[9px] tracking-[0.25em] text-gold">{m.archetype_label}</p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-stone2">
                      <MessageCircle size={12} className="shrink-0" style={{ color: PINK }} />
                      <span className="min-w-0 flex-1 truncate">{m.last_message || "Écrivez le premier mot…"}</span>
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
